import { ErrorMessage } from '@hookform/error-message';
import {
  Box,
  Divider,
  FormControlLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { format } from 'date-fns';
import { FC, useMemo } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { DATE_FORMAT_API } from '../../../../../../../../@constants/date';
import { Error } from '../../../../../../../../components/Common/Error';
import DateRangePicker from '../../../../../../../../components/DateRangePicker';
import { useRootContext } from '../../../../../../../../components/RootContainer';
import { Calendar2Icon } from '../../../../../../../../components/SvgIcons/Calendar2Icon';
import { FileExcelIcon } from '../../../../../../../../components/SvgIcons/FileExcelIcon';
import { FilePdfIcon } from '../../../../../../../../components/SvgIcons/FilePdfIcon';
import { FooterExtraButtons } from '../../../../../../components/FooterButtons';
import { CompanyField } from '../../../../../../components/Inputs/CompanyField';
import { MarketCodeField } from '../../../../../../components/Inputs/MarketCodeField';
import { RoomTypeField } from '../../../../../../components/Inputs/RoomTypeField';
import { SegmentMarketField } from '../../../../../../components/Inputs/SegmentMarketField';
import { ReportLabel } from '../../../../../../components/ReportLabel';
import { AgencyPerfermanceOverviewReportFormModel, GroupedByEnum } from '../../models';
import { CurrencyApplicationField } from './currencyApplicationField';
import { CurrencyField } from './CurrencyField';
import { FixedAtField } from './FixedAtField';

export const FilterForm: FC<{
  onSubmit: (data: AgencyPerfermanceOverviewReportFormModel) => void;
  onReset: () => void;
}> = ({ onSubmit, onReset }) => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setValue,
  } = useFormContext<AgencyPerfermanceOverviewReportFormModel>();

  const formValues = watch();

  // console.log({ formValues });

  const { globalSettings } = useRootContext();
  const dates = useMemo(
    (): (Date | null)[] => [
      formValues?.from ? new Date(formValues?.from) : null,
      formValues?.to ? new Date(formValues?.to) : null,
    ],
    [formValues?.from, formValues?.to]
  );

  const handleChangeDates = (value: (Date | null)[]) => {
    const [from, to] = value;
    const _from = from ? format(from, DATE_FORMAT_API) : null;
    const _to = to ? format(to, DATE_FORMAT_API) : null;
    setValue('from', _from);
    setValue('to', _to);
  };

  return (
    <Box sx={{ p: 0, height: '100%', backgroundColor: 'colors.gray.o10', overflowY: 'scroll' }}>
      <Stack direction={'column'} spacing={0} sx={{ width: '100%', height: '100%' }}>
        <ReportLabel label="Agency performance overview report" />
        <Stack direction={'row'} spacing={0}>
          <Stack
            direction={'column'}
            spacing={2}
            sx={{
              p: 3,
            }}
          >
            <Box sx={{ width: '100%' }}>
              <DateRangePicker
                value={dates}
                onChange={handleChangeDates}
                renderInput={({ startProps, endProps }) => (
                  <Stack
                    direction="row"
                    spacing="1rem"
                    sx={{
                      width: '100%',
                    }}
                  >
                    <TextField
                      {...startProps}
                      size="medium"
                      variant="standard"
                      sx={{
                        width: '50%',
                      }}
                      label="From"
                      error={Boolean(errors?.from)}
                      helperText={errors?.from?.message}
                      placeholder="mm/dd/yyyy"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar2Icon
                              sx={{
                                fontSize: '1rem',
                                color: ({ palette }) => palette.common.black,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                    <TextField
                      {...endProps}
                      size="medium"
                      variant="standard"
                      sx={{
                        width: '50%',
                      }}
                      label="To"
                      error={Boolean(errors?.to)}
                      helperText={errors?.to?.message}
                      placeholder="mm/dd/yyyy"
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <Calendar2Icon
                              sx={{
                                fontSize: '1rem',
                                color: ({ palette }) => palette.common.black,
                              }}
                            />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>
                )}
              />
            </Box>

            <Controller
              control={control}
              name="cards"
              render={({ field }) => (
                <CompanyField value={field.value} onChange={value => field.onChange(value)} />
              )}
            />

            <Controller
              control={control}
              name="room_type_ids"
              render={({ field }) => (
                <RoomTypeField value={field.value} onChange={value => field.onChange(value)} />
              )}
            />

            <Controller
              control={control}
              name="segment_market_code_ids"
              render={({ field }) => (
                <SegmentMarketField value={field.value} onChange={value => field.onChange(value)} />
              )}
            />

            <Controller
              control={control}
              name="market_code_ids"
              render={({ field }) => (
                <MarketCodeField
                  segment_market_code_ids={formValues.segment_market_code_ids}
                  value={field.value}
                  onChange={value => field.onChange(value)}
                />
              )}
            />

            <Controller
              control={control}
              name="with_revenue"
              render={({ field }) => (
                <FormControlLabel
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      With Revenue
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />

            <Controller
              control={control}
              name="currency"
              render={({ field, formState }) => (
                <CurrencyField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  error={
                    formState?.errors[field.name] ? (
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    ) : null
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="currency_application"
              render={({ field, formState }) => (
                <CurrencyApplicationField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  error={
                    formState?.errors[field.name] ? (
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    ) : null
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="fixed_at"
              render={({ field, formState }) => (
                <FixedAtField
                  value={field.value}
                  onChange={value => field.onChange(value)}
                  helperText={
                    <>
                      <ErrorMessage
                        errors={formState.errors}
                        name={field.name}
                        render={({ message }) => <Error>{message}</Error>}
                      />
                    </>
                  }
                />
              )}
            />

            <Controller
              control={control}
              name="gross_revenue"
              render={({ field }) => (
                <FormControlLabel
                  disabled={formValues.with_revenue === false}
                  sx={{ m: 0 }}
                  {...field}
                  checked={field.value ?? false}
                  onChange={(e, checked) => field.onChange(checked)}
                  label={
                    <Typography variant="inter16" fontWeight={500} sx={{ ml: '1rem' }}>
                      Gross Revenue
                    </Typography>
                  }
                  control={<Switch />}
                />
              )}
            />

            <Divider />

            <Controller
              control={control}
              name="grouped_by"
              render={({ field }) => (
                <Stack direction="column" gap={1}>
                  <Typography variant="inter12" fontWeight={600} textTransform="uppercase">
                    Grouped by
                  </Typography>
                  <RadioGroup
                    value={field.value}
                    onChange={value => field.onChange(value)}
                    sx={{
                      gap: 0.5,
                    }}
                  >
                    <FormControlLabel
                      value={GroupedByEnum.NONE}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: theme => theme.typography.inter14,
                        },
                      }}
                      control={<Radio size="small" />}
                      label="None"
                    />
                    <FormControlLabel
                      value={GroupedByEnum.MARKET_CODE}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: theme => theme.typography.inter14,
                        },
                      }}
                      control={<Radio size="small" />}
                      label="Market code"
                    />
                    <FormControlLabel
                      value={GroupedByEnum.MARKET_SEGMENT}
                      sx={{
                        '& .MuiTypography-root': {
                          fontSize: theme => theme.typography.inter14,
                        },
                      }}
                      control={<Radio size="small" />}
                      label="Segment market code"
                    />
                  </RadioGroup>
                </Stack>
              )}
            />

            <Box sx={{ height: 10 }} />
          </Stack>
        </Stack>
        {/* <FooterButtons onSubmit={handleSubmit(onSubmit)} onReset={onReset} /> */}

        <FooterExtraButtons
          onSubmit={file_type => {
            setValue('file_type', file_type);
            handleSubmit(onSubmit)();
          }}
          onReset={onReset}
          exportFileTypes={[
            {
              label: 'PDF',
              value: 'pdf',
              icon: <FilePdfIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
            {
              label: 'EXCEL',
              value: 'excel',
              icon: <FileExcelIcon sx={{ width: '3rem', height: '3rem' }} />,
            },
          ]}
        />
      </Stack>
    </Box>
  );
};
